/* eslint-disable @nx/enforce-module-boundaries */
import {
   ACL_TARGET_ACL,
   ACL_TARGET_ACL_GROUPS,
   ACL_TARGET_APPROVALS,
   ACL_TARGET_BI_REPORTS,
   ACL_TARGET_BUDGET_REVENUE_RESULTS,
   ACL_TARGET_CLIENTS_DASHBOARDS,
   ACL_TARGET_CLIENTS_MEDIABUDGET,
   ACL_TARGET_DEADLINES,
   ACL_TARGET_DEVELOPERS,
   ACL_TARGET_EMPLOYEES,
   ACL_TARGET_INVOICES,
   ACL_TARGET_INVOICES_AGREEMENTS,
   ACL_TARGET_LABELS,
   ACL_TARGET_MEDIAPLANS,
   ACL_TARGET_ORDERS,
   ACL_TARGET_ORDERS_CORRECTIONS,
   ACL_TARGET_ORDERS_PENDING,
   ACL_TARGET_ORDERS_TASKS,
   ACL_TARGET_SUBSCRIPTIONS,
   ACL_TARGET_TIME_REGISTRATION,
   aclPolicyGuard,
   canActivateAuth,
   UserDataGuard,
} from '@abovo/shared-auth';
import { AppRoutes } from '@abovo/shared-models';
import { MediaPlanBriefingModule, PreferencesGuard, StaticReportPageComponent, WebvisComponent, WebvisGuard } from '@abovo/webvis';
import {
   ActualKeyPerformanceIndicatorStore,
   DossierStore,
   FEATURE_NAME_V2,
   INTERMEDIATE_CAMPAIGN_REPORT_FEATURE_KEY,
   OrdersStore,
   reducerMap,
   TeamStore,
} from '@abovo/webvis-data-access';
import { staticReportResolver } from '@abovo/webvis/pages/media-plans/detail/helpers/static-report.resolver';
import { mediaPlanDetailEffects } from '@abovo/webvis/pages/media-plans/detail/store';
import { importProvidersFrom, NgModule } from '@angular/core';
import { ExtraOptions, RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';

const appRoutingModuleName = 'AppRoutingModule';

const APP_ROUTES: AppRoutes = [
   {
      path: 'mapdemo',
      loadChildren: () => import('./pages/mapdemo/mapdemo.routes').then((m) => m.MAPDEMO_ROUTES),
      title: 'Map Demo',
   },
   {
      path: 'campaign-report/:reportId',
      resolve: { data: staticReportResolver },
      component: StaticReportPageComponent,
      canActivate: [UserDataGuard, canActivateAuth],
      title: 'intermediate_report.intermediate_report',
      providers: [
         ActualKeyPerformanceIndicatorStore,
         DossierStore,
         OrdersStore,
         TeamStore,
         {
            provide: INTERMEDIATE_CAMPAIGN_REPORT_FEATURE_KEY,
            useValue: FEATURE_NAME_V2,
         },
         importProvidersFrom(EffectsModule.forFeature(mediaPlanDetailEffects), StoreModule.forFeature(FEATURE_NAME_V2, reducerMap)),
      ],
   },
   {
      path: '',
      component: WebvisComponent,
      canActivate: [UserDataGuard, canActivateAuth, WebvisGuard, PreferencesGuard],
      // The header quick-create menu opens the order wizard and the media plan briefing from any page, so their
      // route-scoped dependencies (OrdersStore, the briefing module's services + NgRx feature) must be in scope here.
      // Should be removed in GER-5567
      providers: [OrdersStore, importProvidersFrom(MediaPlanBriefingModule)],
      children: [
         {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full',
         },
         {
            path: 'dashboard',
            loadComponent: () => import('@abovo/webvis/pages/dashboard/containers/dashboard.component').then((module) => module.DashboardComponent),
            title: 'components.my_dashboard',
            data: {
               pagePath: 'dashboard',
               icon: ['fas', 'star'],
               pageHistoryEntry: true,
            },
         },

         {
            path: 'campaigns',
            redirectTo: 'mediaplans',
         },
         {
            path: 'mediaplans',
            loadChildren: () => import('@abovo/webvis/pages/media-plans/media-plans.module').then((m) => m.MediaPlansModule),
            canActivate: [aclPolicyGuard],
            title: 'media_campaign.campaigns',
            data: {
               // We're assuming that when a user can see multi mediaplans they are also allowed to see single mediaplans and that the oposite is not the case
               policy: { target: ACL_TARGET_MEDIAPLANS(true), operation: 'read', description: `${appRoutingModuleName} mediaplans route` },
               pagePath: 'campaigns',
               icon: ['fak', 'mediaplanicon'],
               pageHistoryEntry: true,
            },
         },
         {
            path: 'colleagues',
            loadChildren: () => import('@abovo/webvis/pages/employee-overview/employee-overview.routing.module').then((m) => m.EMPLOYEE_OVERVIEW_ROUTING),
            canActivate: [aclPolicyGuard],
            title: 'components.colleagues',
            data: {
               icon: ['fas', 'address-book'],
               pageHistoryEntry: true,
               policy: { operation: 'read', target: ACL_TARGET_EMPLOYEES, description: `${appRoutingModuleName} employees route` },
            },
         },
         {
            path: 'user-profile',
            loadChildren: () => import('@abovo/webvis/pages/user-profile/account.module').then((m) => m.AccountModule),
            title: 'components.my_account',
            data: {
               pagePath: 'user-profile',
               pageHistoryEntry: true,
               icon: ['fas', 'user'],
            },
         },
         {
            path: 'budget',
            loadChildren: () => import('@abovo/webvis/pages/am-budget/am-budget.routes').then((m) => m.ACCOUNT_MANAGER_BUDGET_ROUTES),
            canActivate: [aclPolicyGuard],
            title: 'header.account_manager_budget',
            data: {
               pagePath: 'budget',
               pageHistoryEntry: true,
               icon: ['fas', 'file-chart-column'],
               policy: { operation: 'read', target: ACL_TARGET_BUDGET_REVENUE_RESULTS, description: `${appRoutingModuleName} client-account-budget route` },
            },
         },
         {
            path: 'mediabudgets',
            // TODO Removed since we missed some functionality, converting the component
            // loadChildren: () => import('@abovo/webvis/pages/client-budget/client-budget.routing.module').then((m) => m.CLIENT_BUDGET_ROUTING),
            loadChildren: () => import('@abovo/webvis/pages/mc-budget/mc-budget.module').then((m) => m.McBudgetModule),
            canActivate: [aclPolicyGuard],
            title: 'client_budget.component',
            data: {
               pagePath: 'mediabudgets',
               pageHistoryEntry: true,
               icon: ['fas', 'magnifying-glass-dollar'],
               policy: { operation: 'read', target: ACL_TARGET_CLIENTS_MEDIABUDGET, description: `${appRoutingModuleName} client-media-budget route` },
            },
         },
         {
            path: 'crm/account',
            loadChildren: () =>
               import('@abovo/webvis/pages/crm-account-overview/crm-account-overview-routing.module').then((m) => m.CRM_ACCOUNT_OVERVIEW_ROUTING),
            title: 'crm_account.clients_and_prospects',
            data: {
               pagePath: 'crm/account',
               icon: ['fak', 'clients'],
               pageHistoryEntry: true,
            },
         },
         {
            path: 'crm/dashboards',
            loadChildren: () => import('@abovo/webvis/pages/client-dashboard/client-dashboard.routing.module').then((m) => m.CLIENT_DASHBOARD_ROUTING),
            canActivate: [aclPolicyGuard],
            title: 'components.client_dashboard',
            data: {
               pagePath: 'crm/dashboards',
               icon: ['fas', 'users'],
               pageHistoryEntry: true,
               policy: { operation: 'read', target: ACL_TARGET_CLIENTS_DASHBOARDS, description: `${appRoutingModuleName} client-dashboards route` },
            },
         },
         {
            path: 'crm/bi-reports',
            loadChildren: () => import('@abovo/webvis/pages/bi-reports/bi-reports.routing.module').then((m) => m.BI_REPORTS_ROUTING),
            canActivate: [aclPolicyGuard],
            title: 'components.bi_reports',
            data: {
               pagePath: 'crm/bi-reports',
               icon: ['fas', 'chart-bar'],
               pageHistoryEntry: true,
               policy: { operation: 'read', target: ACL_TARGET_BI_REPORTS, description: `${appRoutingModuleName} bi-reports route` },
            },
         },
         {
            path: 'crm/labels',
            loadComponent: () => import('@abovo/webvis/pages/label-manager/containers/label-manager.component').then((m) => m.LabelManagerComponent),
            canActivate: [aclPolicyGuard],
            title: 'label_manager.client_labels',
            data: {
               pagePath: 'crm/labels',
               icon: ['fas', 'users'],
               pageHistoryEntry: true,
               policy: { operation: 'read', target: ACL_TARGET_LABELS, description: `${appRoutingModuleName} labels route` },
            },
         },
         {
            path: 'acl',
            loadChildren: () => import('@abovo/webvis/pages/acl/acl.routing.module').then((m) => m.ACL_ROUTING),
            canActivate: [aclPolicyGuard],
            title: 'components.acl',
            data: {
               pagePath: 'acl',
               icon: ['fas', 'user'],
               pageHistoryEntry: true,
               policy: { operation: 'read', target: ACL_TARGET_ACL, description: `${appRoutingModuleName} acl route` },
            },
         },
         {
            path: 'team-management',
            loadChildren: () => import('@abovo/webvis/pages/user-groups/user-groups.routing.module').then((m) => m.USER_GROUPS_ROUTING),
            canActivate: [aclPolicyGuard],
            title: 'components.team_management',
            data: {
               icon: ['fas', 'people-group'],
               pageHistoryEntry: true,
               policy: { operation: 'update', target: ACL_TARGET_ACL_GROUPS, description: `${appRoutingModuleName} team-management route` },
            },
         },
         {
            path: 'orders',
            loadChildren: () => import('@abovo/webvis/pages/orders/orders.routing.module').then((m) => m.ORDERS_ROUTING),
            canActivate: [aclPolicyGuard],
            title: 'components.orders_overview',
            data: {
               pagePath: 'orders',
               icon: ['fas', 'cart-shopping-fast'],
               pageHistoryEntry: true,
               policy: { operation: 'read', target: ACL_TARGET_ORDERS, description: `${appRoutingModuleName} orders route` },
            },
         },
         {
            path: 'tasks',
            loadChildren: () => import('@abovo/webvis/pages/tasks/tasks-app.module').then((m) => m.TasksAppModule),
            canActivate: [aclPolicyGuard],
            title: 'components.tasks',
            data: {
               pagePath: 'order/tasks',
               icon: ['fas', 'list-check'],
               pageHistoryEntry: true,
               policy: { operation: 'read', target: ACL_TARGET_ORDERS_TASKS, description: `${appRoutingModuleName} tasks route` },
            },
         },
         {
            path: 'order-corrections',
            loadChildren: () => import('@abovo/webvis/pages/order-corrections').then((m) => m.ORDER_CORRECTIONS_ROUTING),
            canActivate: [aclPolicyGuard],
            title: 'components.order_corrections',
            data: {
               icon: ['fas', 'list-check'],
               pageHistoryEntry: true,
               policy: { operation: 'read', target: ACL_TARGET_ORDERS_CORRECTIONS, description: `${appRoutingModuleName} order-corrections route` },
            },
         },
         {
            path: 'pending-administration',
            loadChildren: () => import('@abovo/webvis/pages/pending-administration').then((m) => m.PendingAdministrationModule),
            canActivate: [aclPolicyGuard],
            title: 'components.pending_administration',
            data: {
               icon: ['fas', 'list-check'],
               pageHistoryEntry: true,
               policy: { operation: 'read', target: ACL_TARGET_ORDERS_PENDING, description: `${appRoutingModuleName} pending-administration route` },
            },
         },
         {
            path: 'order/:orderId',
            loadChildren: () => import('@abovo/webvis/pages/order/order.module').then((m) => m.OrderModule),
            canActivate: [aclPolicyGuard],
            data: {
               pagePath: 'order/:orderId',
               policy: { operation: 'read', target: ACL_TARGET_ORDERS, description: `${appRoutingModuleName} single-order route` },
               icon: ['fas', 'cart-shopping-fast'],
               pageHistoryEntry: true,
            },
         },
         {
            path: 'invoice-agreements',
            loadChildren: () => import('@abovo/webvis/pages/invoice-agreements/invoice-agreements.module').then((m) => m.InvoiceAgreementModule),
            canActivate: [aclPolicyGuard],
            title: 'components.invoice_agreements',
            data: {
               policy: { operation: 'read', target: ACL_TARGET_INVOICES_AGREEMENTS, description: `${appRoutingModuleName} invoice-agreements route` },
               pagePath: 'invoice-agreements',
               icon: ['far', 'comments-dollar'],
               pageHistoryEntry: true,
            },
         },
         {
            path: 'subscriptions',
            loadChildren: () => import('@abovo/webvis/pages/subscriptions/subscriptions.routing.module').then((m) => m.SUBSCRIPTION_ROUTING),
            canActivate: [aclPolicyGuard],
            title: 'components.subscriptions',
            data: {
               pagePath: 'subscriptions',
               icon: ['fas', 'money-bill-transfer'],
               pageHistoryEntry: true,
               policy: { operation: 'read', target: ACL_TARGET_SUBSCRIPTIONS, description: `${appRoutingModuleName} subscriptions route` },
            },
         },
         {
            path: 'planning',
            loadChildren: () => import('@abovo/webvis/pages/planning/planning.routes').then((m) => m.PLANNING_ROUTING),
            canActivate: [aclPolicyGuard],
            data: {
               pagePath: 'planning',
               icon: ['fas', 'calendar-days'],
               pageHistoryEntry: true,
               policy: { operation: 'read', target: ACL_TARGET_TIME_REGISTRATION, description: `${appRoutingModuleName} planning route` },
            },
         },
         {
            path: 'approvals',
            loadChildren: () => import('@abovo/webvis/pages/approval/approval.routing.module').then((m) => m.APPROVAL_ROUTING),
            canActivate: [aclPolicyGuard],
            title: 'components.approvals',
            data: {
               policy: { operation: 'read', target: ACL_TARGET_APPROVALS, description: `${appRoutingModuleName} approvals route` },
               pagePath: 'approvals',
               icon: ['far', 'check-double'],
               pageHistoryEntry: true,
            },
         },
         {
            path: 'deadline-overview',
            loadChildren: () => import('@abovo/webvis/pages/deadline-overview/deadline-overview.routing.module').then((m) => m.DEADLINE_OVERVIEW_ROUTING),
            canActivate: [aclPolicyGuard],
            title: 'components.deadline_overview',
            data: {
               policy: { operation: 'read', target: ACL_TARGET_DEADLINES, description: `${appRoutingModuleName} deadline-overview route` },
               pagePath: 'deadline-overview',
               icon: ['far', 'calendar-clock'],
               pageHistoryEntry: true,
            },
         },
         {
            path: 'invoices',
            loadChildren: () => import('@abovo/webvis/pages/invoices/invoices.routing').then((m) => m.INVOICES_ROUTING),
            canActivate: [aclPolicyGuard],
            title: 'components.invoices',
            data: {
               pagePath: 'invoices',
               icon: ['fas', 'file-invoice-dollar'],
               pageHistoryEntry: true,
               policy: { operation: 'read', target: ACL_TARGET_INVOICES, description: `${appRoutingModuleName} invoices route` },
            },
         },
         {
            path: 'versions',
            canActivate: [aclPolicyGuard],
            loadChildren: () => import('@abovo/webvis/pages/hall-of-fame/hall-of-fame.routing').then((m) => m.HALL_OF_FAME_ROUTES),
            title: 'Hall of fame',
            data: {
               pagePath: 'versions',
               pageHistoryEntry: true,
               icon: ['fas', 'face-awesome'],
               policy: { operation: 'read', target: ACL_TARGET_DEVELOPERS, description: `${appRoutingModuleName} developers-versions route` },
            },
         },
         {
            path: 'sandbox',
            canActivate: [aclPolicyGuard],
            title: 'Sandbox and examples',
            data: {
               policy: { operation: 'read', target: ACL_TARGET_DEVELOPERS, description: `${appRoutingModuleName} developers-sandbox route` },
               pageHistoryEntry: true,
            },
            loadChildren: () => import('@abovo/webvis/pages/examples/examples.module').then((m) => m.ExamplesModule),
         },
         {
            path: '**',
            loadComponent: () => import('@abovo/webvis/pages/404/404-page.component').then((m) => m.PageNotFoundComponent),
            title: 'pages.not_found',
            data: {
               icon: ['fas', 'circle-exclamation'],
            },
         },
      ],
   },
];
const ROUTER_SETTINGS: ExtraOptions = {
   useHash: true,
};

@NgModule({
   imports: [RouterModule.forRoot(APP_ROUTES, ROUTER_SETTINGS)],
   exports: [RouterModule],
})
export class AppRoutingModule {}
